// Placement banner component for PC placement phase

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';

/**
 * Placement banner component
 * Displays during PC placement phase with objective
 */
class PlacementBanner extends BaseComponent {
  static properties = {
    show: { type: Boolean },
    message: { type: String },
    objective: { type: String },
  };

  static styles = css`
    :host {
      display: block;
    }

    .placement-banner-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      animation: slideDown 0.3s ease-out;
    }

    .placement-banner {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 24px;
      background: rgba(59, 130, 246, 0.95);
      color: white;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      font-size: 16px;
      font-weight: 500;
      white-space: nowrap;
    }

    .objective-banner {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 6px;
      padding: 12px 20px;
      background: rgba(34, 34, 34, 0.9);
      color: #e5e5e5;
      border-radius: 6px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      max-width: 400px;
      text-align: center;
    }

    .dismiss-button {
      position: absolute;
      top: 8px;
      right: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      padding: 0;
      background: transparent;
      border: none;
      border-radius: 4px;
      color: #a3a3a3;
      cursor: pointer;
      transition:
        color 0.15s,
        background-color 0.15s;
    }

    .dismiss-button:hover {
      color: #ffffff;
      background: rgba(255, 255, 255, 0.15);
    }

    .objective-header {
      display: flex;
      align-items: center;
      gap: 6px;
      color: #a3a3a3;
    }

    .objective-title {
      font-size: 16px;
      font-weight: 500;
    }

    .objective-text {
      font-size: 16px;
      font-weight: 500;
      line-height: 1.4;
    }

    .icon {
      width: 20px;
      height: 20px;
    }

    .icon-sm {
      width: 16px;
      height: 16px;
    }

    .icon-x {
      width: 18px;
      height: 18px;
    }

    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `;

  constructor() {
    super();
    this.show = false;
    this.message = 'Choose Your Starting Position';
    this.objective = '';
  }

  _handleDismissObjective() {
    this.dispatchEvent(new CustomEvent('dismiss-objective', { bubbles: true }));
  }

  _renderMapPinnedIcon() {
    return html`
      <svg
        class="icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M18 8c0 4.5-6 9-6 9s-6-4.5-6-9a6 6 0 0 1 12 0" />
        <circle cx="12" cy="8" r="2" />
        <path
          d="M8.835 14H5a1 1 0 0 0-.9.7l-2 6c-.1.1-.1.2-.1.3 0 .6.4 1 1 1h18c.6 0 1-.4 1-1 0-.1 0-.2-.1-.3l-2-6a1 1 0 0 0-.9-.7h-3.835"
        />
      </svg>
    `;
  }

  _renderTrophyIcon() {
    return html`
      <svg
        class="icon-sm"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
        <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
        <path d="M4 22h16" />
        <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
        <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
        <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
      </svg>
    `;
  }

  _renderXIcon() {
    return html`
      <svg
        class="icon-x"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="3"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M18 6 6 18" />
        <path d="m6 6 12 12" />
      </svg>
    `;
  }

  render() {
    if (!this.show) {
      return html``;
    }

    return html`
      <div class="placement-banner-container">
        <div class="placement-banner">
          ${this._renderMapPinnedIcon()}
          <span>${this.message}</span>
        </div>
        ${
          this.objective
            ? html`
              <div class="objective-banner">
                <button class="dismiss-button" @click="${this._handleDismissObjective}">${this._renderXIcon()}</button>
                <div class="objective-header">
                  ${this._renderTrophyIcon()}
                  <span class="objective-title">Adventure Objective</span>
                </div>
                <span class="objective-text">${this.objective}</span>
              </div>
            `
            : ''
        }
      </div>
    `;
  }
}

customElements.define('placement-banner', PlacementBanner);
